import { lazy } from 'react';

/**
 * React.lazy wrapper that auto-recovers from stale-chunk failures.
 *
 * Vite builds split each route into a hash-named chunk (e.g. page-BdSsgGcy.js).
 * After a new deploy the old hashes no longer exist on the server; a tab still
 * running the PREVIOUS build (e.g. left idle, then the user navigates) requests a
 * chunk that 404s → the SPA host serves index.html (text/html) in its place →
 * the dynamic import fails ("Failed to fetch dynamically imported module" /
 * "Expected a JavaScript module script but the server responded with text/html")
 * → the route renders a BLANK page until the user manually refreshes.
 *
 * We catch that failure and trigger ONE full reload, which fetches the fresh
 * index.html + the current chunk names. A short timestamp guard prevents reload
 * loops when the failure is genuinely persistent (offline / real 5xx).
 */
let reloading = false;

/**
 * Trigger the ONE guarded reload for a stale chunk. Returns true while a reload
 * is under way (the caller should wait for it), false when the guard refused
 * (a reload already happened < 10 s ago: the failure is real, surface it).
 */
export function reloadForStaleChunk() {
  if (reloading) return true;
  const KEY = 'chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last > 10000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    reloading = true;
    window.location.reload();
    return true;
  }
  return false;
}

export function lazyWithReload(importFn) {
  return lazy(() =>
    importFn().catch((error) => {
      if (reloadForStaleChunk()) {
        // Keep Suspense pending (the loading spinner) until the reload happens,
        // instead of surfacing the error to React (which would blank the route).
        return new Promise(() => {});
      }
      throw error;
    })
  );
}

export default lazyWithReload;
